import type {
  GuestSessionBootstrapOptions,
  GuestSessionBootstrapResult,
} from "./types";

const DEFAULT_BOOTSTRAP_PATH = "/v1/auth/guest-session";
const DEFAULT_CHANNEL = "web";
const DEFAULT_BOOTSTRAP_TIMEOUT_MS = 3000;

export async function requestGuestSessionBootstrap({
  backendUrl,
  guestId,
  resetGuest = false,
  signal,
  options = {},
}: {
  backendUrl: string;
  guestId?: string | null;
  resetGuest?: boolean;
  signal?: AbortSignal;
  options?: GuestSessionBootstrapOptions;
}): Promise<GuestSessionBootstrapResult> {
  const fetchImpl = options.fetch ?? globalThis.fetch?.bind(globalThis);
  if (!fetchImpl) {
    throw new Error("Guest session bootstrap requires fetch.");
  }

  const pageContext = buildGuestBootstrapPageContext(options);
  const body = {
    ...(guestId ? { guest_id: guestId } : {}),
    ...(resetGuest ? { reset_guest: true } : {}),
    page_host: pageContext.pageHost,
    page_path: pageContext.pagePath,
    channel: pageContext.channel,
  };

  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.addEventListener("abort", abort, { once: true });
  if (signal?.aborted) controller.abort();
  const timeoutMs = cleanPositiveNumber(options.timeoutMs) ?? DEFAULT_BOOTSTRAP_TIMEOUT_MS;
  const timer = globalThis.setTimeout(() => controller.abort(), timeoutMs);
  let response: Response;
  try {
    response = await fetchImpl(buildGuestBootstrapUrl(backendUrl, options.path), {
      method: "POST",
      headers: {
        accept: "application/json",
        "content-type": "application/json",
      },
      credentials: "include",
      cache: "no-store",
      signal: controller.signal,
      body: JSON.stringify(body),
    });
  } finally {
    globalThis.clearTimeout(timer);
    signal?.removeEventListener("abort", abort);
  }

  if (!response.ok) {
    throw new Error(`Guest session bootstrap failed with status ${response.status}.`);
  }

  const payload = (await response.json()) as Record<string, unknown>;
  return {
    guestId: cleanString(payload.guest_id) ?? null,
  };
}

export function buildGuestBootstrapUrl(backendUrl: string, path = DEFAULT_BOOTSTRAP_PATH): string {
  const location = globalThis.window?.location ?? globalThis.location;
  const baseUrl = backendUrl || location?.origin || "http://localhost";
  try {
    return new URL(path, baseUrl).toString();
  } catch {
    return new URL(path, location?.origin || "http://localhost").toString();
  }
}

function buildGuestBootstrapPageContext(options: GuestSessionBootstrapOptions): {
  pageHost: string | undefined;
  pagePath: string | undefined;
  channel: string;
} {
  const location = globalThis.window?.location ?? globalThis.location;
  return {
    pageHost: cleanString(options.pageContext?.pageHost) ?? cleanString(location?.hostname),
    pagePath: cleanPagePath(options.pageContext?.pagePath ?? location?.pathname),
    channel: cleanString(options.pageContext?.channel) ?? DEFAULT_CHANNEL,
  };
}

function cleanPagePath(value: unknown): string | undefined {
  const path = cleanString(value);
  if (!path) return undefined;
  const withoutQuery = path.split("?")[0]?.split("#")[0] ?? "";
  return withoutQuery.startsWith("/") ? withoutQuery : `/${withoutQuery}`;
}

function cleanString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function cleanPositiveNumber(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) return null;
  return value;
}
